import {
  Check,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Copy,
  Delete,
  Download,
  Ellipsis,
  Eraser,
  FileText,
  Image as ImageIcon,
  Lightbulb,
  Link2,
  Play,
  RefreshCw,
  Save,
  Share2,
  Timer,
  Trash2,
  Trophy,
  UserRound,
  X,
  createIcons,
} from "lucide";

import { explainPuzzle } from "../puzzle/explain.ts";
import { generatePuzzle, utcDateString } from "../puzzle/generator.ts";
import type { Puzzle } from "../puzzle/types.ts";
import { normalizeDisplayName, type GameResponse, type LeaderboardEntry } from "../shared/competition.ts";
import { competitionApi, CompetitionError } from "./competition-api.ts";
import { clearCode, eraseDigit, isDigitUsed, pickDigit } from "./code-entry.ts";
import { trackEvent } from "./analytics.ts";
import { transferUrl } from "./transfer.ts";
import { APP_CONFIG } from "./config.ts";
import {
  glyphMarkup,
  paperForSeed,
  type GlyphManifest,
  type PaperManifest,
  type PaperVariant,
} from "./glyphs.ts";
import { notebookInk } from "./notebook-ink.ts";
import { mobilePaperAspect } from "./notebook-layout.ts";
import { escapeHtml, leaderboardTable } from "./leaderboard.ts";
import { loadPlayerProfile, PLAYER_PROFILE_KEY, savePlayerProfile, shouldAutoPublish, type PlayerProfile } from "./player-profile.ts";
import { playerNameForm } from "./player-name-form.ts";
import { canCopyImage, canShareImage, copyImage, copyResult, puzzleShareUrl, shareImage, shareLink, shareText } from "./share.ts";
import { createResultCard, downloadResultCard, type ResultCard } from "./result-card.ts";
import { resultDescription, resultSummary, type ResultSummary } from "./result-summary.ts";
import {
  attemptLimit,
  beginPuzzle,
  calculateStreak,
  elapsedTime,
  formatElapsed,
  loadState,
  progressForDate,
  sameProgress,
  saveProgress,
  saveState,
  submitAttempt,
  type PuzzleProgress,
  type SavedState,
  type StorageLike,
} from "./storage.ts";
import { strokeLayer } from "./strokes.ts";

interface AssetManifests {
  glyphs: GlyphManifest;
  papers: PaperManifest;
}

type ResultAction = "copy-image" | "share-image" | "copy-text" | "copy-link" | "share-link" | "download";

class MemoryStorage implements StorageLike {
  private readonly values = new Map<string, string>();

  getItem(key: string): string | null {
    return this.values.get(key) ?? null;
  }

  setItem(key: string, value: string): void {
    this.values.set(key, value);
  }
}

function browserStorage(): StorageLike {
  try {
    const storage = window.localStorage;
    const probe = "whats-the-code:probe";
    storage.setItem(probe, "1");
    storage.removeItem(probe);
    return storage;
  } catch {
    return new MemoryStorage();
  }
}

function previousDate(date: string): string {
  return new Date(Date.parse(`${date}T00:00:00.000Z`) - 86_400_000).toISOString().slice(0, 10);
}

function nextDate(date: string): string {
  return new Date(Date.parse(`${date}T00:00:00.000Z`) + 86_400_000).toISOString().slice(0, 10);
}

function displayDate(date: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  }).format(new Date(`${date}T12:00:00.000Z`));
}

function safeUrl(url: string): string {
  return url.replaceAll("'", "%27").replaceAll("\\", "%5C");
}

export class DailyPuzzleApp {
  private readonly root: HTMLDivElement;
  private readonly glyphs: GlyphManifest;
  private readonly papers: PaperManifest;
  private readonly storage: StorageLike;
  private savedState: SavedState;
  private today: string;
  private selectedDate: string;
  private puzzle: Puzzle;
  private progress: PuzzleProgress;
  private digits = ["", "", ""];
  private activeDigit = 0;
  private textMode = false;
  private strokeState: "none" | "wrong" | "solved" | "failed" = "none";
  private statusMessage = "";
  private statusTone: "info" | "error" = "info";
  private clearTimer: number | undefined;
  private clockTimer: number | undefined;
  private busy = false;
  private checkingGame = true;
  private competitionUnavailable = false;
  private publishingName = false;
  private nameDraft: string | undefined;
  private profile: PlayerProfile;
  private readonly autoPublishAttempts = new Set<string>();
  private nameMessage = "";
  private boardEntries: LeaderboardEntry[] = [];
  private boardDate = "";
  private boardLoading = false;
  private boardError = "";
  private boardRequest = 0;
  private syncRequest = 0;
  private serverClockOffsetMs = 0;
  private reviewPuzzle = false;
  private resultCard: ResultCard | undefined;
  private resultCardRequestKey = "";
  private resultCardError = "";
  private resultAction: ResultAction | undefined;

  constructor(root: HTMLDivElement, manifests: AssetManifests) {
    this.root = root;
    this.glyphs = manifests.glyphs;
    this.papers = manifests.papers;
    this.storage = browserStorage();
    this.savedState = loadState(this.storage);
    this.profile = loadPlayerProfile(this.storage, this.savedState);
    this.today = utcDateString(new Date());
    this.selectedDate = this.dateFromLocation();
    this.puzzle = generatePuzzle(this.selectedDate);
    this.progress = progressForDate(this.savedState, this.selectedDate, this.today);
    this.serverClockOffsetMs = this.progress.clockOffsetMs ?? 0;
    this.restoreCompletedDigits();
  }

  start(notice = ""): void {
    if (notice) this.setStatus(notice);
    saveState(this.storage, this.savedState);
    window.addEventListener("popstate", () => this.openDate(this.dateFromLocation(), false));
    this.root.addEventListener("keydown", (event) => {
      if (event.target instanceof Element && event.target.closest("[data-code-form], [data-keypad-panel]")) {
        this.handleKeyDown(event);
      }
    });
    this.root.addEventListener("paste", (event) => {
      if (event.target instanceof Element && event.target.closest("[data-code-form], [data-keypad-panel]")) {
        this.handlePaste(event);
      }
    });
    document.addEventListener("pointerdown", (event) => {
      if (event.target instanceof Element && !event.target.closest(".result-more")) {
        this.root.querySelector(".result-more")?.removeAttribute("open");
      }
    });
    this.render();
    this.startClock();
    window.addEventListener("pagehide", () => window.clearInterval(this.clockTimer));
    window.addEventListener("pageshow", (event) => {
      if (event.persisted) {
        this.startClock();
        void this.syncCompetition();
      }
    });
    document.addEventListener("visibilitychange", () => {
      this.updateClock();
      if (!document.hidden && !this.busy && !this.publishingName) void this.syncCompetition();
    });
    window.addEventListener("storage", (event) => {
      if (event.key === PLAYER_PROFILE_KEY) {
        this.profile = loadPlayerProfile(this.storage);
        if (!this.busy && !this.publishingName) {
          this.render();
          this.autoPublishName();
        }
        return;
      }
      if (event.key !== "whats-the-code:v1" || this.busy || this.publishingName) return;
      const state = loadState(this.storage);
      if (sameProgress(state.progress[this.selectedDate], this.progress)) return;
      this.savedState = state;
      if (this.selectedDate !== this.today && state.progress[this.selectedDate]) {
        this.progress = state.progress[this.selectedDate]!;
        this.restoreCompletedDigits();
        this.render();
      } else {
        void this.syncCompetition();
      }
    });
    void this.syncCompetition();
  }

  private dateFromLocation(): string {
    const value = new URL(window.location.href).searchParams.get("date");
    if (!value) return this.today;
    try {
      const date = utcDateString(value);
      if (date < APP_CONFIG.launchDate || date > this.today) return this.today;
      return date;
    } catch {
      return this.today;
    }
  }

  private restoreCompletedDigits(): void {
    if (this.progress.status === "solved" || this.progress.status === "failed") {
      this.digits = this.puzzle.code.map(String);
      this.strokeState = this.progress.status === "solved" ? "solved" : "failed";
    } else {
      this.digits = ["", "", ""];
      this.strokeState = "none";
    }
    this.activeDigit = 0;
  }

  private render(): void {
    const profileDialog = this.root.querySelector<HTMLDialogElement>("[data-profile-dialog]");
    const profileForm = profileDialog?.querySelector<HTMLFormElement>("[data-profile-form]");
    const profileDraft = profileDialog?.open && profileForm ? new FormData(profileForm) : undefined;
    const paper = paperForSeed(this.papers, this.puzzle.seed);
    const isFirst = this.selectedDate <= APP_CONFIG.launchDate;
    const isToday = this.selectedDate === this.today;
    const finished = this.progress.status === "solved" || this.progress.status === "failed";

    this.root.innerHTML = `
      <div class="app-shell">
        <header class="toolbar" aria-label="Puzzle controls">
          <div class="archive-controls">
            <button class="icon-button" type="button" data-previous ${isFirst ? "disabled" : ""}
              aria-label="Previous puzzle" title="Previous puzzle" ${this.busy ? "disabled" : ""}>
              <i data-lucide="chevron-left" aria-hidden="true"></i>
            </button>
            <label class="date-control" title="Choose an archive date">
              <i data-lucide="calendar-days" aria-hidden="true"></i>
              <span>${displayDate(this.selectedDate)}</span>
              <input type="date" data-date min="${APP_CONFIG.launchDate}" max="${this.today}"
                value="${this.selectedDate}" aria-label="Puzzle date" ${this.busy ? "disabled" : ""} />
            </label>
            <button class="icon-button" type="button" data-next ${isToday ? "disabled" : ""}
              aria-label="Next puzzle" title="Next puzzle" ${this.busy ? "disabled" : ""}>
              <i data-lucide="chevron-right" aria-hidden="true"></i>
            </button>
          </div>
          <p class="puzzle-meta">#${this.puzzle.puzzleNumber} · ${this.puzzle.difficulty.label}</p>
          <div class="toolbar-actions">
          <button class="icon-button" type="button" data-profile aria-label="Your name" title="Your name${this.profile.name ? `: ${escapeHtml(this.profile.name)}` : ""}">
            <i data-lucide="user-round" aria-hidden="true"></i>
          </button>
          <button class="icon-button" type="button" data-leaderboard aria-label="Daily leaderboard" title="Daily leaderboard">
            <i data-lucide="trophy" aria-hidden="true"></i>
          </button>
          <button class="icon-button mode-button" type="button" data-mode
            aria-label="${this.textMode ? "Use notebook mode" : "Use text mode"}"
            title="${this.textMode ? "Use notebook mode" : "Use text mode"}"
            aria-pressed="${this.textMode}">
            <i data-lucide="${this.textMode ? "image" : "file-text"}" aria-hidden="true"></i>
          </button>
          </div>
        </header>

        <main>
          <div class="game-bar">
            ${this.progress.status === "playing"
              ? `<span class="game-attempts" data-status data-tone="${this.statusMessage ? this.statusTone : "info"}" role="status">${escapeHtml(this.playMessage())}</span>`
              : `<span>${this.selectedDate === this.today ? "Today's puzzle" : "Archive puzzle"}${this.progress.ranked ? " · Timed" : ""}</span>`}
            <span class="game-clock"><i data-lucide="timer" aria-hidden="true"></i><span role="timer" aria-live="off" aria-label="Elapsed time" data-elapsed>${formatElapsed(this.elapsed())}</span></span>
          </div>
          ${this.progress.status === "ready" ? this.renderReady(paper) : finished && !this.reviewPuzzle ? this.renderResult() : `
          <section class="paper-stage" ${this.textMode ? "hidden" : ""}
            aria-label="Handwritten puzzle for ${displayDate(this.selectedDate)}">
            <article class="notebook-page ${this.puzzle.clues.length > 4 ? "notebook-dense" : ""}" style="--paper-image:url('${safeUrl(paper.src)}');--mobile-page-aspect:${mobilePaperAspect(this.puzzle.clues.length)}">
              <div class="writing-layer">
                ${this.renderNotebook(paper)}
              </div>
            </article>
          </section>
          <section class="text-puzzle" ${this.textMode ? "" : "hidden"}>
            ${this.renderTextPuzzle()}
          </section>
          `}
          ${this.renderStatus()}
        </main>
        <footer class="site-footer">
          <span>bzwrd</span><span aria-hidden="true">·</span><span>UTC</span>
        </footer>
      </div>
      ${this.renderProfileDialog()}
      ${this.renderWorkingDialog()}
      ${this.renderLeaderboardDialog()}`;

    createIcons({
      icons: {
        Check,
        CalendarDays,
        ChevronLeft,
        ChevronRight,
        Copy,
        Delete,
        Download,
        Ellipsis,
        Eraser,
        FileText,
        Image: ImageIcon,
        Lightbulb,
        Link2,
        Play,
        RefreshCw,
        Save,
        Share2,
        Timer,
        Trash2,
        Trophy,
        UserRound,
        X,
      },
      root: this.root,
    });
    this.bindEvents();
    this.updateResultActions();
    if (profileDraft) {
      const dialog = this.root.querySelector<HTMLDialogElement>("[data-profile-dialog]")!;
      const input = dialog.querySelector<HTMLInputElement>("[data-profile-name]")!;
      input.value = String(profileDraft.get("display-name") ?? "");
      dialog.querySelector<HTMLInputElement>('[name="auto-join"]')!.checked = profileDraft.has("auto-join");
      dialog.showModal();
    }
    this.prepareResultCard();
  }

  private finishedSummary(): ResultSummary | undefined {
    if (this.progress.status !== "solved" && this.progress.status !== "failed") return undefined;
    try {
      return resultSummary(this.puzzle, this.progress, this.profile.name);
    } catch {
      return undefined;
    }
  }

  private currentResultCard(): ResultCard | undefined {
    const summary = this.finishedSummary();
    return summary && this.resultCard?.key === JSON.stringify(summary) ? this.resultCard : undefined;
  }

  private renderResult(): string {
    const summary = this.finishedSummary();
    const details = `<div class="${this.textMode ? "result-text" : "sr-only"}">
      <h1>${APP_CONFIG.name}</h1>
      ${summary?.name ? `<p class="result-player">${escapeHtml(summary.name)}</p>` : ""}
      <h2>${this.progress.status === "solved" ? "Solved!" : "Not solved"}</h2>
      <p>Puzzle #${this.puzzle.puzzleNumber} · ${displayDate(this.selectedDate)}</p>
      <dl><div><dt>Guesses</dt><dd>${this.progress.attempts.length}/${attemptLimit(this.progress)}</dd></div>
      <div><dt>Time</dt><dd>${formatElapsed(this.progress.solveTimeMs ?? 0)}</dd></div></dl>
    </div>`;
    return `<section class="result-view" aria-label="Your result">
      ${details}
      ${this.textMode ? "" : `<div class="result-visual" data-result-visual>${this.renderResultVisual(summary)}</div>`}
    </section>`;
  }

  private renderResultVisual(summary: ResultSummary | undefined): string {
    const card = this.currentResultCard();
    if (card && summary) {
      return `<img class="result-card-image" src="${card.preview}" width="1200" height="1200" alt="${escapeHtml(resultDescription(summary))}" />`;
    }
    return `<div class="result-card-loading">
      ${glyphMarkup(this.glyphs, "title", this.puzzle.seed, 0, "result-loading-title")}
      <p role="status">${this.resultCardError || !summary ? "The result image could not be prepared." : "Preparing your result card..."}</p>
      ${this.resultCardError && summary ? '<button class="quiet-button" type="button" data-retry-card>Try again</button>' : ""}
    </div>`;
  }

  private prepareResultCard(): void {
    const summary = this.finishedSummary();
    if (!summary) return;
    const key = JSON.stringify(summary);
    if (key === this.resultCardRequestKey) return;
    this.resultCardRequestKey = key;
    this.resultCardError = "";
    void createResultCard(summary).then((card) => {
      if (this.resultCardRequestKey !== key) return;
      this.resultCard = card;
      this.updateResultVisual();
    }).catch(() => {
      if (this.resultCardRequestKey !== key) return;
      this.resultCardError = "Could not prepare the result image.";
      this.updateResultVisual();
    });
  }

  private updateResultVisual(): void {
    const visual = this.root.querySelector<HTMLElement>("[data-result-visual]");
    if (visual) visual.innerHTML = this.renderResultVisual(this.finishedSummary());
    this.updateResultActions();
    this.bindCardRetry();
  }

  private updateResultActions(): void {
    const card = this.currentResultCard();
    this.root.querySelectorAll<HTMLButtonElement>("[data-export-action]").forEach((button) => {
      const action = button.dataset.exportAction as ResultAction;
      const needsImage = action === "copy-image" || action === "share-image" || action === "download";
      const supported = action === "copy-image" ? canCopyImage()
        : action === "share-image" ? Boolean(card && canShareImage(card.file))
        : action === "share-link" ? typeof navigator.share === "function" : true;
      button.disabled = Boolean(this.resultAction) || (needsImage && !card) || !supported;
      button.setAttribute("aria-busy", String(this.resultAction === action));
      if (action === "copy-image") button.title = supported ? "Copy the result as an image" : "Image copying is unavailable. Download the image instead.";
      if (action === "share-image") button.title = !card || supported ? "Share the result as an image" : "Image sharing is unavailable. Copy or download the image instead.";
    });
  }

  private bindCardRetry(): void {
    this.root.querySelector("[data-retry-card]")?.addEventListener("click", () => {
      this.resultCardRequestKey = "";
      this.resultCardError = "";
      this.render();
    }, { once: true });
  }

  private renderReady(paper: PaperVariant): string {
    return `<section class="paper-stage" aria-label="Ready to begin">
      <article class="notebook-page ready-page" style="--paper-image:url('${safeUrl(paper.src)}')">
        <h1 class="ready-brand"><span class="sr-only">${APP_CONFIG.name}: ${APP_CONFIG.tagline}</span>${glyphMarkup(this.glyphs, "title", this.puzzle.seed, 0, "ready-title")}</h1>
        <p class="ready-tagline" aria-hidden="true">${glyphMarkup(this.glyphs, "tagline", this.puzzle.seed, 0, "ready-tagline-glyph")}</p>
        <div class="ready-content">
          <p class="ready-number">#${this.puzzle.puzzleNumber} · ${displayDate(this.selectedDate)}</p>
          <h2>Ready to begin?</h2>
          <button class="begin-button" type="button" data-begin ${this.busy || this.checkingGame ? "disabled" : ""}>
            <i data-lucide="play" aria-hidden="true"></i>${this.busy ? "Starting..." : this.checkingGame ? "Getting ready..." : "Begin puzzle"}
          </button>
          ${this.competitionUnavailable && !this.busy && this.selectedDate === this.today ? '<button class="quiet-button" type="button" data-practice>Play unranked</button>' : ""}
          <div class="ready-player"><button class="quiet-button" type="button" data-profile><i data-lucide="user-round" aria-hidden="true"></i>${this.profile.name ? escapeHtml(this.profile.name) : "Add your name"}</button></div>
          <button class="ready-leaderboard" type="button" data-leaderboard><i data-lucide="trophy" aria-hidden="true"></i>Daily leaderboard</button>
        </div>
      </article>
    </section>`;
  }

  private renderNotebook(paper: PaperVariant): string {
    const result = this.progress.status === "playing" || this.progress.solveTimeMs === undefined
      ? undefined
      : {
          solved: this.progress.status === "solved",
          value: this.progress.status === "solved"
            ? formatElapsed(this.progress.solveTimeMs)
            : this.puzzle.code.join(""),
        };
    return `
      <h1 class="sr-only">${APP_CONFIG.name}: ${APP_CONFIG.tagline}</h1>
      <ol class="sr-only">${this.puzzle.clues.map((clue) => `<li>${clue.guess.join("")}: ${clue.feedback.phrase}</li>`).join("")}</ol>
      ${notebookInk({ glyphs: this.glyphs, paper, puzzle: this.puzzle, result })}
      ${strokeLayer(this.puzzle.seed, this.strokeState)}
      ${this.renderVisualCodeEntry()}
      ${this.renderKeypad(true)}`;
  }

  private renderVisualCodeEntry(): string {
    const reveal = this.progress.status === "failed" && this.strokeState !== "wrong";
    const disabled = this.entryLocked() ? "disabled" : "";
    const slots = this.digits
      .map((digit, index) => {
        const glyph = digit
          ? glyphMarkup(
              this.glyphs,
              `digit-${digit}`,
              this.puzzle.seed,
              100 + index,
              `entry-digit-glyph${reveal ? " reveal-glyph" : ""}`,
            )
          : "";
        return `
          <button type="button" class="code-cell" data-code-index="${index}" ${disabled}
            aria-pressed="${index === this.activeDigit}" aria-label="Code digit ${index + 1}: ${digit || "empty"}">
            <span class="code-glyph-slot" data-code-glyph="${index}">${glyph}</span>
            <svg class="code-selection-mark" viewBox="0 0 100 12" aria-hidden="true"><path d="M8 7 Q43 3 92 6 M13 10 Q57 6 86 9" /></svg>
          </button>`;
      })
      .join("");

    return `<div class="visual-code-form" data-code-form role="group" aria-label="Three-digit code">${slots}</div>`;
  }

  private entryLocked(): boolean {
    return this.progress.status !== "playing" || this.busy || this.strokeState === "wrong";
  }

  private renderAnswerSlots(): string {
    return this.digits.map((digit, index) => `<button type="button" class="answer-slot"
      data-code-index="${index}" aria-pressed="${index === this.activeDigit}"
      aria-label="Code digit ${index + 1}: ${digit || "empty"}" ${this.entryLocked() ? "disabled" : ""}>
      <span data-code-text="${index}" aria-hidden="true">${digit || "&nbsp;"}</span>
    </button>`).join("");
  }

  private playMessage(): string {
    const remaining = attemptLimit(this.progress) - this.progress.attempts.length;
    return this.statusMessage || `${remaining} ${remaining === 1 ? "attempt" : "attempts"} left`;
  }

  private setStatus(message: string, tone: "info" | "error" = "info"): void {
    this.statusMessage = message;
    this.statusTone = message ? tone : "info";
  }

  // Problems with a guess are repeated beside the keypad, where the player is looking.
  private entryNotice(): string {
    return this.progress.status === "playing" && this.statusTone === "error" ? this.statusMessage : "";
  }

  private renderKeypad(handwritten: boolean): string {
    if (this.progress.status !== "playing") return "";
    const buttons = Array.from({ length: 10 }, (_, digit) => {
      const used = isDigitUsed({ digits: this.digits, activeDigit: this.activeDigit }, String(digit));
      return `<button type="button" class="keypad-key" data-keypad="${digit}" data-used="${used}" aria-label="Enter ${digit}"
        ${this.entryLocked() || used ? "disabled" : ""}>
        ${handwritten ? glyphMarkup(this.glyphs, `digit-${digit}`, this.puzzle.seed, 180 + digit, "choice-digit-glyph") : digit}
        ${handwritten ? '<svg class="choice-mark" viewBox="0 0 60 50" aria-hidden="true"><path class="choice-ring" d="M45 7 C59 11 55 43 33 46 C12 49 3 36 7 21 C11 4 34 1 45 7" /><path class="choice-strike" d="M13 35 Q26 21 47 14" /></svg>' : ""}
      </button>`;
    }).join("");
    const ready = this.digits.every(Boolean) && !this.entryLocked();
    const canErase = this.digits.some(Boolean) && !this.entryLocked();
    return `
      <div class="${handwritten ? "notebook-keypad" : "text-keypad"}" data-keypad-panel role="group" aria-label="Choose code digits">
          <div class="keypad-digits" role="group" aria-label="Available digits">${buttons}</div>
          <div class="keypad-actions">
            <div class="entry-corrections">
              <button class="icon-button" type="button" data-erase ${canErase ? "" : "disabled"} title="Backspace" aria-label="Backspace"><i data-lucide="delete" aria-hidden="true"></i></button>
              <button class="icon-button" type="button" data-clear ${canErase ? "" : "disabled"} title="Clear code" aria-label="Clear code"><i data-lucide="eraser" aria-hidden="true"></i></button>
            </div>
            <button type="button" class="check-button" data-submit aria-label="Check code" aria-busy="${this.busy}" ${ready ? "" : "disabled"}>
              ${handwritten
                ? glyphMarkup(this.glyphs, "action-check", this.puzzle.seed, 0, "check-glyph")
                : `<i data-lucide="check" aria-hidden="true"></i>${this.busy ? "Checking..." : "Check code"}`}
            </button>
          </div>
          <p class="entry-notice" data-entry-notice role="alert" ${this.entryNotice() ? "" : "hidden"}>${escapeHtml(this.entryNotice())}</p>
      </div>`;
  }

  private renderTextPuzzle(): string {
    const clues = this.puzzle.clues
      .map(
        (clue) => `<li><strong>${clue.guess.join("")}</strong><span>${clue.feedback.phrase}</span></li>`,
      )
      .join("");
    return `
      <header class="text-heading">
        <h1>${APP_CONFIG.name}</h1>
        <p>Puzzle #${this.puzzle.puzzleNumber} · ${displayDate(this.selectedDate)} · ${this.puzzle.difficulty.label}</p>
      </header>
      <ol class="text-clues">${clues}</ol>
      <div class="text-code-form" data-code-form role="group" aria-label="Three-digit code">
        <div class="answer-slots">${this.renderAnswerSlots()}</div>
      </div>
      ${this.renderKeypad(false)}`;
  }

  private renderStatus(): string {
    if (this.progress.status === "playing") return "";
    let message = this.statusMessage;
    if (!message && this.progress.status === "solved") {
      const streak = calculateStreak(this.savedState, this.today);
      message = `Solved in ${formatElapsed(this.progress.solveTimeMs ?? 0)} · ${this.progress.attempts.length}/${attemptLimit(this.progress)} · ${streak} day streak`;
    }
    if (!message && this.progress.status === "failed") {
      message = `Not solved · ${this.progress.attempts.length}/${attemptLimit(this.progress)} · ${formatElapsed(this.progress.solveTimeMs ?? 0)}`;
    }

    const finished = this.progress.status === "solved" || this.progress.status === "failed";
    const actions = finished
        ? `<div class="result-actions" role="group" aria-label="Share your result">
            <button type="button" data-copy-image data-export-action="copy-image"><i data-lucide="copy" aria-hidden="true"></i>Copy image</button>
            <button type="button" data-share data-export-action="share-image"><i data-lucide="share-2" aria-hidden="true"></i>Share image</button>
            <details class="result-more">
              <summary class="icon-button" aria-label="More result options" title="More result options"><i data-lucide="ellipsis" aria-hidden="true"></i></summary>
              <div class="result-options">
                <button type="button" data-save-result data-export-action="download"><i data-lucide="download" aria-hidden="true"></i>Download image</button>
                <button type="button" data-copy-result data-export-action="copy-text"><i data-lucide="file-text" aria-hidden="true"></i>Copy result text</button>
                <button type="button" data-copy-link data-export-action="copy-link"><i data-lucide="link-2" aria-hidden="true"></i>Copy puzzle link</button>
                <button type="button" data-share-link data-export-action="share-link"><i data-lucide="share-2" aria-hidden="true"></i>Share puzzle link</button>
              </div>
            </details>
          </div>
          <div class="result-secondary-actions">
            <button type="button" data-review-puzzle><i data-lucide="${this.reviewPuzzle ? "image" : "file-text"}" aria-hidden="true"></i>${this.reviewPuzzle ? "Show result card" : "Review puzzle"}</button>
            <button type="button" data-working><i data-lucide="lightbulb" aria-hidden="true"></i>${this.progress.status === "failed" ? "Show the answer" : "Show working"}</button>
            <button type="button" data-leaderboard><i data-lucide="trophy" aria-hidden="true"></i>Leaderboard</button>
          </div>` : "";
    return `<section class="status-area"><p data-status role="status">${escapeHtml(message)}</p>${actions}${this.renderNameForm()}</section>`;
  }

  private renderNameForm(): string {
    if (this.progress.status !== "solved" || !this.progress.ranked || this.selectedDate !== this.today) {
      return this.progress.status === "ready" ? "" : playerNameForm(this.profile, "result-player-name", this.nameMessage);
    }
    const joined = Boolean(this.progress.leaderboardName);
    return `<form class="leaderboard-entry" data-score-form>
      <label for="leaderboard-name">Display name <span>(public)</span></label>
      <div class="name-controls">
        <input id="leaderboard-name" name="display-name" data-player-name type="text" minlength="2" maxlength="24"
          required autocomplete="nickname" value="${escapeHtml(this.nameDraft ?? this.progress.leaderboardName ?? this.profile.name)}" ${this.publishingName ? "disabled" : ""} />
        <button type="submit" ${this.publishingName ? "disabled" : ""}><i data-lucide="trophy" aria-hidden="true"></i>${this.publishingName ? "Saving..." : joined ? "Update name" : "Join leaderboard"}</button>
        ${joined ? `<button class="icon-button" type="button" data-remove-name title="Remove my name" aria-label="Remove my name" ${this.publishingName ? "disabled" : ""}><i data-lucide="trash-2" aria-hidden="true"></i></button>` : ""}
      </div>
      ${this.renderAutoJoinPreference()}
      <p role="status" class="name-status">${escapeHtml(this.nameMessage || (joined ? "Your time is on the leaderboard." : ""))}</p>
    </form>`;
  }

  private renderAutoJoinPreference(): string {
    return `<label class="auto-join-preference"><input type="checkbox" data-auto-join ${this.profile.autoJoin ? "checked" : ""} ${this.publishingName ? "disabled" : ""} />Automatically post my future solves</label>`;
  }

  private renderProfileDialog(): string {
    return `<dialog class="working-dialog profile-dialog" data-profile-dialog aria-labelledby="profile-title">
      <header><h2 id="profile-title">Your name</h2><button class="icon-button" type="button" data-close-profile
        aria-label="Close name settings" title="Close name settings"><i data-lucide="x" aria-hidden="true"></i></button></header>
      ${playerNameForm(this.profile, "profile-player-name")}
      <p class="transfer-restore">Played at code.bzwrd.co.uk before? <a href="${transferUrl(`${window.location.pathname}${window.location.search}`)}" data-restore-history>Bring your history and name across</a>.</p>
    </dialog>`;
  }

  private saveLocalName(form: HTMLFormElement): void {
    const fields = new FormData(form);
    const name = normalizeDisplayName(fields.get("display-name"));
    if (!name) {
      form.querySelector<HTMLElement>("[data-profile-status]")!.textContent = "Use a name of 2 to 24 letters, numbers, spaces, or simple punctuation.";
      form.querySelector<HTMLInputElement>("[data-profile-name]")?.focus();
      return;
    }
    this.profile = { name, autoJoin: fields.has("auto-join") };
    savePlayerProfile(this.storage, this.profile);
    this.nameDraft = undefined;
    this.nameMessage = "Name saved in this browser.";
    this.root.querySelector<HTMLDialogElement>("[data-profile-dialog]")?.close();
    this.render();
    this.root.querySelector<HTMLElement>(form.closest("dialog") ? "[data-profile]" : "[data-profile-name]")?.focus({ preventScroll: true });
  }

  private renderLeaderboardDialog(): string {
    return `<dialog class="working-dialog leaderboard-dialog" data-leaderboard-dialog aria-labelledby="leaderboard-title">
      <header><div><h2 id="leaderboard-title">Daily leaderboard</h2><p>${displayDate(this.selectedDate)} · UTC</p></div>
        <div class="dialog-actions">
          <button class="icon-button" type="button" data-refresh-board aria-label="Refresh leaderboard" title="Refresh leaderboard"><i data-lucide="refresh-cw" aria-hidden="true"></i></button>
          <button class="icon-button" type="button" data-close-board aria-label="Close leaderboard" title="Close leaderboard"><i data-lucide="x" aria-hidden="true"></i></button>
        </div>
      </header>
      <div data-board-content role="region" aria-label="Rankings">${this.boardContent()}</div>
    </dialog>`;
  }

  private boardContent(): string {
    if (this.boardLoading) return '<p role="status" class="leaderboard-empty">Loading times...</p>';
    if (this.boardError) return `<p role="status" class="leaderboard-empty">${escapeHtml(this.boardError)}</p>`;
    return leaderboardTable(this.boardDate === this.selectedDate ? this.boardEntries : [], this.progress.runId);
  }

  private renderWorkingDialog(): string {
    if (this.progress.status !== "solved" && this.progress.status !== "failed") return "";
    const working = explainPuzzle(this.puzzle);
    const steps = working.steps
      .map((step, index) => {
        const previous = working.steps[index - 1]?.code ?? [null, null, null];
        const changed = step.code.some((digit, position) => digit !== previous[position]);
        const label = step.code.map((digit, position) => `${["first", "middle", "last"][position]} box ${digit ?? "unknown"}`).join(", ");
        const code = changed ? `<div class="working-code" role="img" aria-label="Code so far: ${label}">
          ${step.code.map((digit) => `<span aria-hidden="true">${digit ?? "?"}</span>`).join("")}</div>` : "";
        return `<li class="working-step"><h3>${escapeHtml(step.title)}</h3>${step.paragraphs.map((paragraph) => `<p>${escapeHtml(paragraph)}</p>`).join("")}${code}</li>`;
      })
      .join("");
    const checks = working.checks
      .map((entry) => `<li><strong>${entry.guess}</strong> ${escapeHtml(entry.note)}</li>`)
      .join("");
    return `
      <dialog class="working-dialog" data-working-dialog aria-labelledby="working-title">
        <header><h2 id="working-title">Worked solution</h2><button class="icon-button" type="button" data-close-dialog
          aria-label="Close worked solution" title="Close"><i data-lucide="x" aria-hidden="true"></i></button></header>
        <p>Here is one way to work through the clues.</p>
        <ol class="working-steps">${steps}</ol>
        <p class="working-solution">The code is <strong data-working-code>${this.puzzle.code.join("")}</strong>. It fits every clue.</p>
        <p class="working-check-title">Check it against every clue</p>
        <ul class="working-checks">${checks}</ul>
      </dialog>`;
  }

  private bindEvents(): void {
    this.bindCardRetry();
    this.root.querySelectorAll<HTMLButtonElement>("[data-profile]").forEach((button) => {
      button.addEventListener("click", () => {
        this.root.querySelector<HTMLDialogElement>("[data-profile-dialog]")?.showModal();
      });
    });
    this.root.querySelector("[data-close-profile]")?.addEventListener("click", () => {
      this.root.querySelector<HTMLDialogElement>("[data-profile-dialog]")?.close();
    });
    this.root.querySelectorAll<HTMLFormElement>("[data-profile-form]").forEach((form) => {
      form.addEventListener("submit", (event) => {
        event.preventDefault();
        this.saveLocalName(form);
      });
    });
    this.root.querySelector("[data-review-puzzle]")?.addEventListener("click", () => {
      this.reviewPuzzle = !this.reviewPuzzle;
      this.render();
      this.root.querySelector<HTMLElement>("main")?.scrollIntoView({ block: "start", behavior: "instant" });
    });
    this.root.querySelectorAll<HTMLButtonElement>("[data-export-action]").forEach((button) => {
      button.addEventListener("click", () => {
        this.root.querySelector(".result-more")?.removeAttribute("open");
        void this.exportResult(button.dataset.exportAction as ResultAction);
      });
    });
    this.root.querySelector(".result-more")?.addEventListener("keydown", (event) => {
      if ((event as KeyboardEvent).key !== "Escape") return;
      event.preventDefault();
      this.root.querySelector(".result-more")?.removeAttribute("open");
      this.root.querySelector<HTMLElement>(".result-more summary")?.focus();
    });
    this.root.querySelector<HTMLButtonElement>("[data-begin]")?.addEventListener("click", () => { void this.beginGame(); });
    this.root.querySelector<HTMLButtonElement>("[data-practice]")?.addEventListener("click", () => { void this.beginGame(true); });
    this.root.querySelectorAll<HTMLButtonElement>("[data-leaderboard]").forEach((button) => {
      button.addEventListener("click", () => {
        this.root.querySelector<HTMLDialogElement>("[data-leaderboard-dialog]")?.showModal();
        void this.loadLeaderboard();
        trackEvent("leaderboard_opened");
      });
    });
    this.root.querySelector("[data-close-board]")?.addEventListener("click", () => {
      this.root.querySelector<HTMLDialogElement>("[data-leaderboard-dialog]")?.close();
    });
    this.root.querySelector("[data-refresh-board]")?.addEventListener("click", () => { void this.loadLeaderboard(); });
    this.root.querySelector<HTMLFormElement>("[data-score-form]")?.addEventListener("submit", (event) => {
      event.preventDefault();
      void this.publishName();
    });
    this.root.querySelector("[data-remove-name]")?.addEventListener("click", () => { void this.publishName(true); });
    this.root.querySelector<HTMLInputElement>("[data-player-name]")?.addEventListener("input", (event) => {
      this.nameDraft = (event.currentTarget as HTMLInputElement).value;
    });
    this.root.querySelectorAll<HTMLInputElement>("[data-auto-join]").forEach((input) => {
      input.addEventListener("change", () => {
        this.profile = { ...this.profile, autoJoin: input.checked };
        savePlayerProfile(this.storage, this.profile);
        this.autoPublishName();
      });
    });
    this.root.querySelector<HTMLButtonElement>("[data-previous]")?.addEventListener("click", () => {
      this.openDate(previousDate(this.selectedDate));
    });
    this.root.querySelector<HTMLButtonElement>("[data-next]")?.addEventListener("click", () => {
      this.openDate(nextDate(this.selectedDate));
    });
    this.root.querySelector<HTMLInputElement>("[data-date]")?.addEventListener("change", (event) => {
      this.openDate((event.currentTarget as HTMLInputElement).value);
    });
    this.root.querySelector<HTMLButtonElement>("[data-mode]")?.addEventListener("click", () => {
      this.textMode = !this.textMode;
      this.render();
      this.focusDigit(this.activeDigit);
    });

    this.root.querySelectorAll<HTMLButtonElement>("[data-code-index]").forEach((slot) => {
      const selectSlot = (): void => {
        if (this.entryLocked()) return;
        this.activeDigit = Number(slot.dataset.codeIndex ?? 0);
        this.syncCodeControls();
      };
      slot.addEventListener("focus", selectSlot);
      slot.addEventListener("click", selectSlot);
    });
    this.root.querySelectorAll("[data-erase]").forEach((button) => button.addEventListener("click", () => this.eraseCode()));
    this.root.querySelectorAll("[data-clear]").forEach((button) => button.addEventListener("click", () => this.eraseCode(true)));
    this.root.querySelectorAll<HTMLButtonElement>("[data-keypad]").forEach((button) => {
      button.addEventListener("click", () => this.enterDigit(button.dataset.keypad ?? ""));
    });
    this.root.querySelectorAll<HTMLButtonElement>('[data-submit][type="button"]').forEach((button) => {
      button.addEventListener("click", () => { void this.checkCode(); });
    });
    this.root.querySelector<HTMLButtonElement>("[data-working]")?.addEventListener("click", () => {
      this.root.querySelector<HTMLDialogElement>("[data-working-dialog]")?.showModal();
      trackEvent("working_viewed", { result: this.progress.status });
    });
    this.root.querySelector<HTMLButtonElement>("[data-close-dialog]")?.addEventListener("click", () => {
      this.root.querySelector<HTMLDialogElement>("[data-working-dialog]")?.close();
    });
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (this.entryLocked() || event.ctrlKey || event.metaKey || event.altKey) return;
    if (/^\d$/.test(event.key)) {
      event.preventDefault();
      this.enterDigit(event.key);
      return;
    }
    if (event.key === "Backspace") {
      event.preventDefault();
      this.eraseCode();
      return;
    }
    if (event.key === "Delete") {
      event.preventDefault();
      this.digits[this.activeDigit] = "";
      this.syncCodeControls();
      return;
    }
    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      event.preventDefault();
      const change = event.key === "ArrowLeft" ? -1 : 1;
      this.activeDigit = Math.max(0, Math.min(APP_CONFIG.codeLength - 1, this.activeDigit + change));
      this.syncCodeControls();
      this.focusDigit(this.activeDigit);
      return;
    }
    if (event.key === "Enter" && event.target instanceof Element && event.target.closest("[data-code-index]")) {
      event.preventDefault();
      void this.checkCode();
    }
  }

  private handlePaste(event: ClipboardEvent): void {
    if (this.entryLocked()) return;
    const pasted = event.clipboardData?.getData("text").replace(/\D/g, "").slice(0, 3) ?? "";
    if (!pasted || new Set(pasted).size !== pasted.length) return;
    event.preventDefault();
    this.digits = Array.from({ length: APP_CONFIG.codeLength }, (_, index) => pasted[index] ?? "");
    this.activeDigit = Math.min(APP_CONFIG.codeLength - 1, pasted.length);
    this.syncCodeControls();
    this.focusDigit(this.activeDigit);
  }

  private enterDigit(digit: string): void {
    if (this.entryLocked()) return;
    const next = pickDigit({ digits: this.digits, activeDigit: this.activeDigit }, digit);
    this.digits = next.digits;
    this.activeDigit = next.activeDigit;
    this.clearEntryNotice();
    this.syncCodeControls();
  }

  private eraseCode(clearAll = false): void {
    if (this.entryLocked()) return;
    const next = clearAll ? clearCode(APP_CONFIG.codeLength) : eraseDigit({ digits: this.digits, activeDigit: this.activeDigit });
    this.digits = next.digits;
    this.activeDigit = next.activeDigit;
    this.clearEntryNotice();
    this.syncCodeControls();
  }

  private syncCodeControls(): void {
    this.root.querySelectorAll<HTMLButtonElement>("[data-code-index]").forEach((slot) => {
      const index = Number(slot.dataset.codeIndex ?? 0);
      slot.setAttribute("aria-label", `Code digit ${index + 1}: ${this.digits[index] || "empty"}`);
      slot.setAttribute("aria-pressed", String(index === this.activeDigit));
    });
    this.root.querySelectorAll<HTMLElement>("[data-code-text]").forEach((slot) => {
      slot.textContent = this.digits[Number(slot.dataset.codeText)] || "\u00a0";
    });
    this.root.querySelectorAll<HTMLElement>("[data-code-glyph]").forEach((slot) => {
      const index = Number(slot.dataset.codeGlyph ?? 0);
      const digit = this.digits[index];
      slot.innerHTML = digit
        ? glyphMarkup(
            this.glyphs,
            `digit-${digit}`,
            this.puzzle.seed,
            100 + index,
            "entry-digit-glyph",
          )
        : "";
    });
    this.root.querySelectorAll<HTMLButtonElement>("[data-submit]").forEach((button) => {
      button.disabled = !this.digits.every(Boolean) || this.entryLocked();
    });
    this.root.querySelectorAll<HTMLButtonElement>("[data-keypad]").forEach((button) => {
      const used = isDigitUsed({ digits: this.digits, activeDigit: this.activeDigit }, button.dataset.keypad ?? "");
      button.disabled = this.entryLocked() || used;
      button.dataset.used = String(used);
    });
    this.root.querySelectorAll<HTMLButtonElement>("[data-erase], [data-clear]").forEach((button) => {
      button.disabled = this.entryLocked() || !this.digits.some(Boolean);
    });
  }

  private focusDigit(index: number): void {
    const sectionSelector = this.textMode ? ".text-puzzle" : ".paper-stage";
    const slots = this.root.querySelectorAll<HTMLButtonElement>(
      `${sectionSelector} [data-code-index]`,
    );
    slots[index]?.focus({ preventScroll: true });
  }

  private async checkCode(): Promise<void> {
    if (this.entryLocked() || !this.digits.every(Boolean)) return;
    const guess = this.digits.join("");
    if (new Set(guess).size !== APP_CONFIG.codeLength) {
      this.setStatus("Enter three different digits.", "error");
      this.updateStatusMessage();
      return;
    }
    if (this.progress.attempts.includes(guess)) {
      this.setStatus("You already tried that code.", "error");
      this.updateStatusMessage();
      return;
    }

    const solution = this.puzzle.code.join("");
    let resolveLocally = !(this.progress.ranked && this.selectedDate === this.today);
    let downgraded = false;
    if (!resolveLocally) {
      const date = this.selectedDate;
      this.busy = true;
      this.setStatus("Checking...");
      this.render();
      try {
        const response = await competitionApi.guess(date, guess);
        this.applyServerRun(response);
        if (this.selectedDate !== date) return;
      } catch (error) {
        const status = error instanceof CompetitionError ? error.status : 0;
        this.setStatus(error instanceof Error ? error.message : "Could not check the answer.", "error");
        const recovered = await this.recoverRankedGuess(status, date);
        trackEvent("guess_failed", { status: status || "network", recovered });
        if (!recovered) {
          this.busy = false;
          this.render();
          return;
        }
        resolveLocally = true;
        downgraded = true;
      } finally {
        this.busy = false;
      }
    }
    if (resolveLocally) {
      this.progress = submitAttempt(this.progress, guess, solution, Date.now() + (this.progress.clockOffsetMs ?? 0));
      if (this.selectedDate !== this.today) this.progress.countsForStreak = false;
    }
    this.savedState = saveProgress(this.storage, this.progress);
    if (this.progress.status === "solved" || this.progress.status === "failed") {
      trackEvent("puzzle_finished", { result: this.progress.status, ...this.gameProps() });
    }

    if (this.progress.status === "solved") {
      this.strokeState = "solved";
      this.setStatus("");
      this.reviewPuzzle = false;
      this.render();
      this.root.querySelector<HTMLElement>("main")?.scrollIntoView({ block: "start", behavior: "instant" });
      this.autoPublishName();
      return;
    }

    this.strokeState = "wrong";
    const remaining = attemptLimit(this.progress) - this.progress.attempts.length;
    const verdict = this.progress.status === "failed" ? "That was the final attempt." : `Not that one. ${remaining} ${remaining === 1 ? "attempt" : "attempts"} left.`;
    this.setStatus(downgraded ? `${verdict} This game is now unranked.` : verdict);
    this.render();

    const delay = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 40 : 680;
    window.clearTimeout(this.clearTimer);
    this.clearTimer = window.setTimeout(() => {
      if (this.progress.status === "failed") {
        this.digits = this.puzzle.code.map(String);
        this.strokeState = "failed";
        this.setStatus("");
      } else {
        this.digits = ["", "", ""];
        this.activeDigit = 0;
        this.strokeState = "none";
      }
      this.render();
      if (this.progress.status === "playing") this.focusDigit(0);
      else this.root.querySelector<HTMLElement>("main")?.scrollIntoView({ block: "start", behavior: "instant" });
    }, delay);
  }

  // A rejected ranked guess usually means the session cookie or the day has changed.
  // Resync with the server; when no timed run remains, the game continues unranked
  // so the player can keep going instead of being stuck with a code that never checks.
  private async recoverRankedGuess(status: number, date: string): Promise<boolean> {
    if (status !== 403 && status !== 409) return false;
    const message = this.statusMessage;
    await this.syncCompetition();
    if (this.selectedDate !== date || this.progress.status !== "playing") return false;
    if (this.progress.ranked) return false;
    if (this.statusMessage === message) this.setStatus("");
    return true;
  }

  private updateStatusMessage(): void {
    const status = this.root.querySelector<HTMLElement>("[data-status]");
    if (status) {
      status.textContent = this.progress.status === "playing" ? this.playMessage() : this.statusMessage;
      status.dataset.tone = this.statusMessage ? this.statusTone : "info";
    }
    const notice = this.entryNotice();
    this.root.querySelectorAll<HTMLElement>("[data-entry-notice]").forEach((element) => {
      element.textContent = notice;
      element.hidden = !notice;
    });
  }

  private clearEntryNotice(): void {
    if (this.statusTone !== "error") return;
    this.setStatus("");
    this.updateStatusMessage();
  }

  private openDate(date: string, updateHistory = true): void {
    try { utcDateString(date); } catch { return; }
    if (date < APP_CONFIG.launchDate || date > this.today) return;
    window.clearTimeout(this.clearTimer);
    this.savedState = loadState(this.storage);
    this.selectedDate = date;
    this.puzzle = generatePuzzle(date);
    this.progress = progressForDate(this.savedState, date, this.today);
    this.setStatus("");
    this.nameDraft = undefined;
    this.nameMessage = "";
    this.reviewPuzzle = false;
    this.resultCardError = "";
    this.resultCardRequestKey = this.resultCard?.key ?? "";
    this.checkingGame = date === this.today && this.progress.status === "ready";
    this.activeDigit = 0;
    this.restoreCompletedDigits();
    this.savedState = saveProgress(this.storage, this.progress);
    if (updateHistory) {
      const target = date === this.today ? "/" : `/?date=${date}`;
      window.history.pushState({}, "", target);
    }
    this.render();
    void this.syncCompetition();
  }

  private elapsed(): number {
    return elapsedTime(this.progress, Date.now() + (this.progress.clockOffsetMs ?? 0));
  }

  private startClock(): void {
    window.clearInterval(this.clockTimer);
    this.clockTimer = window.setInterval(() => this.updateClock(), 250);
    this.updateClock();
  }

  private updateClock(): void {
    const clock = this.root.querySelector<HTMLElement>("[data-elapsed]");
    if (clock) clock.textContent = formatElapsed(this.elapsed());
    const today = utcDateString(new Date(Date.now() + this.serverClockOffsetMs));
    if (today !== this.today && !this.busy) {
      const waitingForToday = this.selectedDate === this.today && this.progress.status === "ready";
      this.today = today;
      if (waitingForToday) this.openDate(today);
      else this.render();
    }
  }

  private applyServerRun(response: GameResponse): void {
    const run = response.run;
    if (!run) return;
    const storedProgress = loadState(this.storage).progress[run.date];
    const progress: PuzzleProgress = {
      date: run.date,
      status: run.status,
      attempts: run.attempts,
      maxAttempts: run.maxAttempts ?? APP_CONFIG.maxAttempts,
      startedAt: run.startedAt,
      completedAt: run.finishedAt ?? undefined,
      solveTimeMs: run.elapsedMs ?? undefined,
      countsForStreak: run.date === this.today,
      timingVersion: 2,
      ranked: true,
      runId: run.id,
      leaderboardName: run.name ?? undefined,
      leaderboardAutoJoinSuppressed: !run.name && Boolean(storedProgress?.leaderboardAutoJoinSuppressed),
      clockOffsetMs: response.serverNow - Date.now(),
    };
    this.serverClockOffsetMs = progress.clockOffsetMs ?? 0;
    this.savedState = saveProgress(this.storage, progress);
    if (run.name && !this.profile.name && this.profile.autoJoin) {
      this.profile = { name: run.name, autoJoin: true };
      savePlayerProfile(this.storage, this.profile);
    }
    if (this.selectedDate === run.date) this.progress = progress;
  }

  private async syncCompetition(): Promise<void> {
    const requestId = ++this.syncRequest;
    let date = this.selectedDate;
    let needsRender = this.checkingGame;
    if (date !== this.today) {
      this.checkingGame = false;
      if (needsRender) this.render();
      return;
    }
    try {
      const response = await competitionApi.game();
      if (requestId !== this.syncRequest || this.selectedDate !== date) return;
      this.serverClockOffsetMs = response.serverNow - Date.now();
      if (response.date !== this.today) {
        this.today = response.date;
        this.selectedDate = response.date;
        date = response.date;
        this.puzzle = generatePuzzle(response.date);
        this.progress = progressForDate(this.savedState, response.date, response.date);
        this.restoreCompletedDigits();
        needsRender = true;
      }
      if (response.run) {
        const previous = this.progress;
        this.applyServerRun(response);
        if (!sameProgress(previous, this.progress)) {
          needsRender = true;
          if (previous.status !== this.progress.status || previous.attempts.length !== this.progress.attempts.length) {
            window.clearTimeout(this.clearTimer);
            this.restoreCompletedDigits();
            this.setStatus("");
          }
        }
      } else if (this.progress.ranked) {
        this.progress.ranked = false;
        this.savedState = saveProgress(this.storage, this.progress);
        this.statusMessage = "Your timed session could not be restored. This game is unranked.";
        needsRender = true;
      }
      if (this.competitionUnavailable) needsRender = true;
      this.competitionUnavailable = false;
    } catch {
      if (requestId !== this.syncRequest || this.selectedDate !== date) return;
      if (!this.competitionUnavailable) needsRender = true;
      this.competitionUnavailable = true;
    } finally {
      if (requestId === this.syncRequest && this.selectedDate === date) {
        this.checkingGame = false;
        if (needsRender) this.render();
        else this.updateClock();
        this.autoPublishName();
      }
    }
  }

  private async beginGame(unranked = false): Promise<void> {
    if (this.progress.status !== "ready" || this.busy || this.checkingGame) return;
    this.syncRequest += 1;
    const date = this.selectedDate;
    this.setStatus("");
    if (date !== this.today || unranked) {
      this.progress = { ...beginPuzzle(this.progress, this.today), ranked: false };
      this.savedState = saveProgress(this.storage, this.progress);
    } else {
      this.busy = true;
      this.render();
      try {
        this.applyServerRun(await competitionApi.begin(date));
      } catch (error) {
        this.competitionUnavailable = true;
        this.setStatus(error instanceof Error ? error.message : "Could not start the puzzle.", "error");
        trackEvent("start_failed", { status: (error instanceof CompetitionError && error.status) || "network" });
      } finally {
        this.busy = false;
      }
    }
    if (this.selectedDate !== date) return;
    if (this.progress.status === "playing") trackEvent("puzzle_started", this.gameProps());
    this.restoreCompletedDigits();
    this.render();
    if (this.progress.status === "playing") this.focusDigit(0);
    else this.autoPublishName();
  }

  // Anonymous context for analytics: never names, guesses, scores, or times.
  private gameProps(): { mode: "daily" | "archive"; ranked: boolean } {
    return { mode: this.selectedDate === this.today ? "daily" : "archive", ranked: Boolean(this.progress.ranked) };
  }

  private async loadLeaderboard(): Promise<void> {
    const requestId = ++this.boardRequest;
    const date = this.selectedDate;
    this.boardLoading = true;
    this.boardError = "";
    this.updateBoard();
    try {
      const response = await competitionApi.board(date);
      if (requestId !== this.boardRequest || this.selectedDate !== date) return;
      this.boardEntries = response.entries;
      this.boardDate = response.date;
    } catch (error) {
      if (requestId !== this.boardRequest) return;
      this.boardError = error instanceof Error ? error.message : "Could not load the leaderboard.";
    } finally {
      if (requestId === this.boardRequest) {
        this.boardLoading = false;
        this.updateBoard();
      }
    }
  }

  private updateBoard(): void {
    const content = this.root.querySelector<HTMLElement>("[data-board-content]");
    if (content) content.innerHTML = this.boardContent();
    const refresh = this.root.querySelector<HTMLButtonElement>("[data-refresh-board]");
    if (refresh) refresh.disabled = this.boardLoading;
  }

  private autoPublishName(): void {
    if (this.publishingName || this.busy || this.checkingGame || this.competitionUnavailable) return;
    if (!shouldAutoPublish(this.progress, this.profile, this.today)) return;
    const key = `${this.progress.runId}/${this.profile.name}`;
    if (this.autoPublishAttempts.has(key)) return;
    this.autoPublishAttempts.add(key);
    void this.publishName(false, true);
  }

  private async publishName(remove = false, automatic = false): Promise<void> {
    if (this.publishingName || !this.progress.ranked || this.progress.status !== "solved" || this.selectedDate !== this.today) return;
    const input = this.root.querySelector<HTMLInputElement>("[data-player-name]");
    const name = normalizeDisplayName(automatic ? this.profile.name : input?.value ?? this.nameDraft ?? this.profile.name);
    if (!remove && name === undefined) {
      this.nameMessage = "Use a name of 2 to 24 letters, numbers, spaces, or simple punctuation.";
      this.render();
      this.root.querySelector<HTMLInputElement>("[data-player-name]")?.focus();
      return;
    }
    const date = this.selectedDate;
    if (!automatic) this.nameDraft = input?.value ?? this.nameDraft;
    if (remove) {
      this.progress = { ...this.progress, leaderboardAutoJoinSuppressed: true };
      this.savedState = saveProgress(this.storage, this.progress);
    } else if (!automatic) {
      this.profile = { name: name!, autoJoin: this.profile.autoJoin };
      savePlayerProfile(this.storage, this.profile);
    }
    this.syncRequest += 1;
    this.publishingName = true;
    this.nameMessage = automatic ? `Adding your time as ${name}...` : "";
    this.render();
    try {
      const response = remove ? await competitionApi.remove(date) : await competitionApi.publish(date, name!);
      this.applyServerRun(response);
      this.nameMessage = remove ? "Your name has been removed for today." : `Your time is on the leaderboard as ${name}.`;
      if (!remove) this.nameDraft = name!;
      if (!remove) trackEvent("leaderboard_joined", { automatic });
    } catch (error) {
      this.nameMessage = automatic
        ? "Your result is saved, but automatic posting failed. Use Join leaderboard to retry."
        : error instanceof Error ? error.message : "Could not save your name.";
    } finally {
      this.publishingName = false;
      if (this.selectedDate === date) this.render();
    }
  }

  private async exportResult(action: ResultAction): Promise<void> {
    const summary = this.finishedSummary();
    const card = this.currentResultCard();
    if (!summary || this.resultAction) return;
    if ((action === "copy-image" || action === "share-image" || action === "download") && !card) return;
    const key = JSON.stringify(summary);
    const text = action === "copy-text" ? shareText(this.puzzle, this.progress, this.profile.name) : puzzleShareUrl(summary.date);
    this.resultAction = action;
    this.updateResultActions();
    let message: string | undefined;
    let shared = true;
    try {
      switch (action) {
        case "copy-image":
          await copyImage(card!.file);
          message = "Image copied.";
          break;
        case "share-image": {
          const outcome = await shareImage(card!.file);
          shared = outcome === "shared";
          if (outcome === "shared") message = "Image sent to the share sheet.";
          if (outcome === "unavailable") message = "Image sharing is unavailable. Copy or download the image instead.";
          break;
        }
        case "download":
          downloadResultCard(card!);
          message = "Image download started.";
          break;
        case "copy-text":
        case "copy-link":
          await copyResult(text);
          message = action === "copy-text" ? "Result text copied." : "Puzzle link copied.";
          break;
        case "share-link": {
          const outcome = await shareLink(summary.date);
          shared = outcome === "shared";
          if (outcome === "shared") message = "Puzzle link sent to the share sheet.";
          if (outcome === "unavailable") message = "Link sharing is unavailable. Copy the puzzle link instead.";
          break;
        }
      }
      if (shared) trackEvent("result_shared", { method: action, result: this.progress.status });
    } catch {
      message = action === "copy-image" ? "Image copying was blocked. Download the image instead."
        : action === "copy-text" || action === "copy-link" ? "Copying was blocked by this browser."
        : action === "download" ? "The image download could not be started."
        : "Sharing failed. Use a copy or download option instead.";
    } finally {
      this.resultAction = undefined;
      this.updateResultActions();
      if (message && JSON.stringify(this.finishedSummary()) === key) {
        this.statusMessage = message;
        this.updateStatusMessage();
      }
    }
  }
}